import { createApi } from '@reduxjs/toolkit/query/react';
import { type AxiosBaseQueryMeta, axiosBaseQuery } from 'modules/api/utils';
import { UrlNames } from 'modules/app/constants';
import { configService } from 'modules/app/services';
import { parseRetryAfter } from 'modules/authentication/utils';

import type { MagicLinkParams, OtpRequestParams, OtpVerifyParams } from './types';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

const API_VERSION = 'v1';

// Prefer calling the passwordless auth-api SAME-ORIGIN. Where the frontend CDN forwards
// `/v1/auth/magic-link` to the auth-api (see infrastructure-cdk2 natter-frontend-stack +
// auth-api interstitial-stack), the send response's `flow_id` cookie then lands on the page's
// own host — the same host `/auth/confirm/check` reads it from — so the same-browser interstitial
// skip works without a cross-subdomain cookie. We use the current origin unless an explicit
// VITE_APP_PASSWORDLESS_AUTH_API_URL is configured (envs not yet fronting the send keep their
// cross-origin host); clearing that var is how an env opts into same-origin.
const getBaseURL = () => {
  const configuredAuthApi = configService.getUrl(UrlNames.AuthApi);
  const base = configuredAuthApi || window.location.origin;
  return `${base}/${API_VERSION}`;
};

export const authenticationApi = createApi({
  reducerPath: 'authenticationApi',
  baseQuery: axiosBaseQuery({ baseURL: getBaseURL }),
  endpoints: (builder) => ({
    requestMagicLink: builder.mutation<void, MagicLinkParams>({
      query: (data) => ({
        url: '/auth/magic-link',
        method: 'POST',
        // Opt every magic-link request into the scanner-safe interstitial wrap (NAT-669). The
        // auth-api's server-side `interstitialDefault` stays false; the frontend enables the
        // feature here, at the request level, by always sending `useInterstitial: true`.
        data: { ...data, useInterstitial: true },
        // Send credentials so the browser stores the cross-subdomain `flow_id` cookie set by
        // the auth-api response. That cookie binds the magic link to this browser and enables
        // the interstitial's silent-redirect path. Without it the cookie is dropped and users
        // fall back to the explicit "Continue" page. Safe: the auth-api returns
        // `Access-Control-Allow-Credentials: true` with a specific allowed origin.
        withCredentials: true,
      }),
      // Fold the rate-limit `Retry-After` onto the error itself. The base query surfaces the
      // raw response on `meta`, but `.unwrap()` (used by useResendEmail) propagates only the
      // error payload — so the resend cooldown can read `retryAfterSeconds` only if it lives on
      // the error object. Yields `null` when the header is absent/unparseable (the caller then
      // applies its default cooldown).
      transformErrorResponse: (error: FetchBaseQueryError, meta?: AxiosBaseQueryMeta) => ({
        ...error,
        retryAfterSeconds: parseRetryAfter(meta?.response?.headers),
      }),
    }),
    // NO withCredentials on either OTP mutation: the code-only OTP flow has no same-browser
    // cookie binding to preserve (unlike magic-link's flow_id cookie).
    requestOtp: builder.mutation<{ sent: boolean; cid: string }, OtpRequestParams>({
      query: (data) => ({ url: '/auth/otp/request', method: 'POST', data }),
      // NO transformErrorResponse — request always returns 200; there is no 429 here.
    }),
    verifyOtp: builder.mutation<{ redirectUrl: string }, OtpVerifyParams>({
      query: (data) => ({ url: '/auth/otp/verify', method: 'POST', data }),
      // Fold retryAfterSeconds onto EVERY verify error (null when no header); .unwrap() drops
      // meta, so the 429 cooldown can only read it from the error object. Consumed ONLY on the
      // 429 (rate_limited) branch.
      transformErrorResponse: (error: FetchBaseQueryError, meta?: AxiosBaseQueryMeta) => ({
        ...error,
        retryAfterSeconds: parseRetryAfter(meta?.response?.headers),
      }),
    }),
  }),
});

export const { useRequestMagicLinkMutation, useRequestOtpMutation, useVerifyOtpMutation } = authenticationApi;
